import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Brain, Sparkles, Package, AlertCircle, CheckCircle2, Clock, ArrowRight, Trash2, X, ChevronLeft, Download, FileJson, FileText, Star } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { QuestionTypeSelector } from '@/components/seo/QuestionTypeSelector';
import { QuestionPreviewWithAnswers } from '@/components/seo/QuestionPreviewWithAnswers';
import { API_URL } from '@/config/api';

interface DataSource {
  id: string;
  name: string;
  source_type: string;
  status: string;
  created_at: string;
  data: any;
}

interface QuestionWheel {
  id: string;
  site_id: string;
  source_file_name: string;
  question_count: number;
  created_at: string;
  status: string;
}

interface QuestionSource {
  id: string;
  name: string;
  type: 'data_source' | 'question_wheel';
  questionCount: number;
}

interface GenerationJob {
  id: string;
  generation_type: string;
  status: string;
  total_questions: number;
  processed_questions: number;
  approved_answers: number;
  rejected_answers: number;
  created_at: string;
  wheel_name?: string;
}

interface ProgressUpdate {
  jobId: string;
  status: string;
  message: string;
  current?: number;
  total?: number;
  percentage?: number;
  currentQuestion?: string;
  skippedCount?: number;
  answersGenerated?: number;
  error?: string;
}

interface QuestionCategory {
  key: string;
  label: string;
  description: string;
  types: string[];
  count: number;
  generation_type: string;
}

interface Question {
  id: string;
  question_text: string;
  answer_type: string;
  search_volume?: number;
  opportunity_score?: number;
  answer_id?: string;
  answer?: string;
  vote?: 'upvote' | 'downvote' | 'neutral';
  feedback?: string;
  voted_at?: string;
  source_type?: string;
  confidence_score?: number;
  approval_status?: string;
}

export default function AnswerGeneration() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const location = useLocation();

  const [questionSources, setQuestionSources] = useState<QuestionSource[]>([]);
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [transposing, setTransposing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const [progressUpdate, setProgressUpdate] = useState<ProgressUpdate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<GenerationJob | null>(null);
  const [deleteAnswers, setDeleteAnswers] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Regeneration warning dialog state
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [existingAnswersInfo, setExistingAnswersInfo] = useState<{
    existingCount: number;
    totalQuestions: number;
    wheelName: string;
  } | null>(null);
  const [pendingGenerationType, setPendingGenerationType] = useState<'generic_ai' | 'product_facts' | 'seo_expert' | null>(null);

  // Enhanced Question Wheel workflow state
  const [selectedWheelId, setSelectedWheelId] = useState<string | null>(null);
  const [categories, setCategories] = useState<QuestionCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [viewMode, setViewMode] = useState<'select-wheel' | 'select-category' | 'preview-questions'>('select-wheel');

  useEffect(() => {
    if (selectedSite?.id) {
      fetchQuestionSources();
      fetchJobs();
    }
  }, [selectedSite]);

  // Socket.IO connection for real-time progress updates
  useEffect(() => {
    // Connect to backend Socket.IO server through the proxy
    // Use window.location.origin to connect through the same proxy as API calls
    const socketUrl = process.env.NODE_ENV === 'production'
      ? window.location.origin
      : 'http://localhost:3001';

    const socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['polling', 'websocket'], // Try polling first, then upgrade to websocket
      autoConnect: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[AnswerGeneration] Socket.IO connected');
    });

    socket.on('answer-generation-progress', (data: ProgressUpdate) => {
      console.log('[AnswerGeneration] Progress update:', data);
      setProgressUpdate(data);

      // Auto-refresh jobs list when generation completes
      if (data.status === 'completed' || data.status === 'failed') {
        setTimeout(() => {
          fetchJobs();
          setProgressUpdate(null);
        }, 2000);
      }
    });

    socket.on('disconnect', () => {
      console.log('[AnswerGeneration] Socket.IO disconnected');
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []);

  // Handle incoming navigation state (from Semantic Map or Question Wheel Evaluation)
  useEffect(() => {
    const state = location.state as any;

    // Handle filtered questions from Semantic Map
    if (state?.fromSemanticMap && state?.wheelId && state?.filteredQuestionIds) {
      console.log('[AnswerGeneration] Loading from semantic map:', state);
      setSelectedWheelId(state.wheelId);
      fetchQuestionsByIds(state.wheelId, state.filteredQuestionIds, state.filterContext);
      // Clear location state to prevent re-loading on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
    // Handle direct wheel selection (from Question Wheel Evaluation page)
    else if (state?.fromQuestionWheel && state?.wheelId) {
      console.log('[AnswerGeneration] Loading from question wheel evaluation:', state);
      setSelectedWheelId(state.wheelId);
      setSelectedSourceId(state.wheelId);
      fetchCategories(state.wheelId);
      fetchJobs(state.wheelId);
      // Clear location state to prevent re-loading on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchQuestionSources = async () => {
    try {
      // Fetch both data sources and question wheels in parallel
      const [dataSourcesRes, questionWheelsRes] = await Promise.all([
        authFetch(`/api/data-sources/list/${selectedSite?.id}`),
        authFetch(`/api/answer-generation/question-wheels?siteId=${selectedSite?.id}`)
      ]);

      if (!dataSourcesRes.ok || !questionWheelsRes.ok) {
        throw new Error('Failed to fetch question sources');
      }

      const dataSourcesData = await dataSourcesRes.json();
      const questionWheelsData = await questionWheelsRes.json();

      // Convert data sources with questions to QuestionSource
      const sourcesFromDataSources: QuestionSource[] = (dataSourcesData.dataSources || [])
        .filter((ds: DataSource) =>
          ds.source_type === 'search_intent' ||
          ds.source_type === 'technical_review' ||
          (ds.data?.questions && ds.data.questions.length > 0)
        )
        .map((ds: DataSource) => ({
          id: ds.id,
          name: ds.name || `${ds.source_type} - ${new Date(ds.created_at).toLocaleDateString()}`,
          type: 'data_source' as const,
          questionCount: ds.data?.questions?.length || 0
        }));

      // Convert question wheels to QuestionSource
      const sourcesFromWheels: QuestionSource[] = (questionWheelsData.questionWheels || [])
        .map((qw: QuestionWheel) => ({
          id: qw.id,
          name: `Question Wheel: ${qw.source_file_name || 'Unknown'} (${qw.question_count} questions)`,
          type: 'question_wheel' as const,
          questionCount: parseInt(qw.question_count?.toString() || '0')
        }));

      // Combine and set
      setQuestionSources([...sourcesFromWheels, ...sourcesFromDataSources]);
    } catch (err) {
      console.error('Error fetching question sources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load question sources');
    }
  };

  const fetchJobs = async (wheelId?: string | null) => {
    try {
      let url = `/api/answer-generation/jobs?siteId=${selectedSite?.id}`;
      if (wheelId) {
        url += `&wheelId=${wheelId}`;
      }
      const response = await authFetch(url);
      if (!response.ok) throw new Error('Failed to fetch jobs');

      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const handleGenerateAnswers = async (generationType: 'generic_ai' | 'product_facts' | 'seo_expert') => {
    console.log('[AnswerGeneration] handleGenerateAnswers called', {
      generationType,
      selectedSourceId,
      selectedWheelId,
      selectedSite: selectedSite?.id
    });

    if (!selectedSourceId) {
      console.error('[AnswerGeneration] No selectedSourceId!');
      setError('Please select a question source');
      return;
    }

    // Check for existing answers before proceeding (unless forceRegenerate is checked)
    if (!forceRegenerate) {
      try {
        const checkResponse = await authFetch(
          `/api/answer-generation/check-existing?siteId=${selectedSite?.id}&questionSourceId=${selectedSourceId}`
        );
        const checkData = await checkResponse.json();

        if (checkData.hasExisting) {
          // Show warning dialog instead of proceeding
          setExistingAnswersInfo({
            existingCount: checkData.existingCount,
            totalQuestions: checkData.totalQuestions,
            wheelName: checkData.wheelName
          });
          setPendingGenerationType(generationType);
          setRegenerateDialogOpen(true);
          return;
        }
      } catch (err) {
        console.warn('[AnswerGeneration] Could not check existing answers:', err);
        // Continue anyway if check fails
      }
    }

    // Proceed with generation
    await executeGeneration(generationType);
  };

  const executeGeneration = async (generationType: 'generic_ai' | 'product_facts' | 'seo_expert') => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setProgressUpdate(null); // Clear any previous progress

    try {
      console.log('[AnswerGeneration] Sending request to create job');
      const response = await authFetch('/api/answer-generation/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite?.id,
          questionSourceId: selectedSourceId,
          generationType,
          forceRegenerate, // Include force regenerate flag
          config: {
            model: generationType === 'generic_ai' ? 'gpt-4o-mini' : 'gpt-4o',
            temperature: generationType === 'generic_ai' ? 0.7 : 0.3
          }
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create generation job');
      }

      const data = await response.json();
      setSuccess(`Answer generation started! Job ID: ${data.job.id}`);

      // Refresh jobs list
      setTimeout(() => {
        fetchJobs();
      }, 1000);

    } catch (err) {
      console.error('Error creating generation job:', err);
      setError(err instanceof Error ? err.message : 'Failed to start answer generation');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    setRegenerateDialogOpen(false);
    setForceRegenerate(true); // Enable force regenerate
    if (pendingGenerationType) {
      await executeGeneration(pendingGenerationType);
    }
    setPendingGenerationType(null);
    setExistingAnswersInfo(null);
  };

  const handleTransposeToProducts = async () => {
    // Use selectedWheelId (from category view) or selectedSourceId (from quick tab)
    const wheelId = selectedWheelId || selectedSourceId;

    if (!wheelId || !selectedSite?.id) {
      setError('Please select a question wheel');
      return;
    }

    setTransposing(true);
    setError(null);
    setSuccess(null);

    try {
      // Get the category (wheel center_term) from questionSources
      // The wheel name IS the category (e.g., "Bicycle Wheels")
      const selectedWheel = questionSources.find(s => s.id === wheelId);
      const category = selectedWheel?.name || 'Unknown';

      console.log('[AnswerGeneration] Transposing questions to products', {
        wheelId,
        siteId: selectedSite.id,
        category,
        wheelName: selectedWheel?.name
      });

      const response = await authFetch('/api/answer-generation/transpose-to-products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wheelId,
          siteId: selectedSite.id,
          category,
          limit: 20 // Process up to 20 products
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to transpose questions');
      }

      const data = await response.json();
      setSuccess(`Generated ${data.questionsGenerated} product-specific questions for ${data.productsProcessed} products!`);

    } catch (err) {
      console.error('Error transposing questions:', err);
      setError(err instanceof Error ? err.message : 'Failed to transpose questions to products');
    } finally {
      setTransposing(false);
    }
  };

  const handleOpenDeleteDialog = (job: GenerationJob) => {
    setJobToDelete(job);
    setDeleteAnswers(false); // Reset checkbox
    setDeleteDialogOpen(true);
  };

  const handleDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      const deleteAnswersParam = deleteAnswers ? '?deleteAnswers=true' : '';
      const response = await authFetch(`/api/answer-generation/jobs/${jobToDelete.id}${deleteAnswersParam}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete job');
      }

      const data = await response.json();
      setSuccess(data.message);
      setDeleteDialogOpen(false);
      setJobToDelete(null);

      // Refresh jobs list
      fetchJobs();
    } catch (err) {
      console.error('Error deleting job:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete job');
      setDeleteDialogOpen(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-gray-500',
      processing: 'bg-blue-500',
      completed: 'bg-green-500',
      failed: 'bg-red-500',
      cancelled: 'bg-orange-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getProgress = (job: GenerationJob) => {
    if (job.total_questions === 0) return 0;
    return Math.round((job.processed_questions / job.total_questions) * 100);
  };

  // Enhanced workflow functions
  const fetchCategories = async (wheelId: string) => {
    setLoadingCategories(true);
    setError(null);
    try {
      const response = await authFetch(`${API_URL}/question-wheels/${wheelId}/answer-type-stats`);
      if (!response.ok) throw new Error('Failed to fetch categories');

      const data = await response.json();
      setCategories(data.categories || []);
      setViewMode('select-category');

      // Set selectedSourceId to the wheel ID so generation works
      setSelectedSourceId(wheelId);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load question categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchQuestions = async (wheelId: string, category: string) => {
    setLoadingQuestions(true);
    setError(null);
    try {
      const response = await authFetch(
        `${API_URL}/question-wheels/${wheelId}/questions-by-type?category=${category}&limit=100`
      );
      if (!response.ok) throw new Error('Failed to fetch questions');

      const data = await response.json();
      setQuestions(data.questions || []);
      setViewMode('preview-questions');
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const fetchQuestionsByIds = async (wheelId: string, questionIds: string[], filterContext?: any) => {
    setLoadingQuestions(true);
    setError(null);
    try {
      const response = await authFetch(
        `${API_URL}/question-wheels/${wheelId}/questions-by-ids`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ questionIds })
        }
      );
      if (!response.ok) throw new Error('Failed to fetch questions');

      const data = await response.json();
      setQuestions(data.questions || []);
      setViewMode('preview-questions');
      setSelectedCategory('semantic_map_filtered'); // Special category indicator

      // Show success message with filter context
      if (filterContext) {
        setSuccess(
          `Loaded ${data.questions.length} questions from semantic map ` +
          `(similarity threshold: ${filterContext.similarityThreshold}%)`
        );
      }
    } catch (err) {
      console.error('Error fetching questions by IDs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load filtered questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleSelectQuestion = (questionId: string) => {
    const newSet = new Set(selectedQuestions);
    if (newSet.has(questionId)) {
      newSet.delete(questionId);
    } else {
      newSet.add(questionId);
    }
    setSelectedQuestions(newSet);
  };

  const handleSelectAll = () => {
    const allQuestionIds = new Set(questions.map(q => q.id));
    setSelectedQuestions(allQuestionIds);
  };

  const handleClearSelection = () => {
    setSelectedQuestions(new Set());
  };

  const handleBackToWheelSelection = () => {
    setViewMode('select-wheel');
    setSelectedCategory(null);
    setCategories([]);
    setQuestions([]);
    setSelectedQuestions(new Set());
    fetchJobs(); // Clear filter and show all jobs
  };

  const handleBackToCategorySelection = () => {
    setViewMode('select-category');
    setQuestions([]);
    setSelectedQuestions(new Set());
  };

  const handleExportJSON = async () => {
    try {
      const response = await authFetch(
        `/api/answer-generation/export/json?siteId=${selectedSite?.id}`
      );

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qa-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Q&A data exported to JSON successfully');
    } catch (err) {
      console.error('Error exporting JSON:', err);
      setError('Failed to export JSON');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await authFetch(
        `/api/answer-generation/export/csv?siteId=${selectedSite?.id}`
      );

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qa-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Q&A data exported to CSV successfully');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV');
    }
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to generate answers
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">AI Answer Generation</h1>
        <p className="text-muted-foreground">
          Generate answers to questions using AI with or without product context
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">{success}</AlertDescription>
        </Alert>
      )}

      {/* Real-time Progress Display */}
      {progressUpdate && (
        <Alert className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-pulse" />
          <div className="flex-1 space-y-3">
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{progressUpdate.message}</span>
                {progressUpdate.percentage !== undefined && (
                  <span className="text-sm">{progressUpdate.percentage}%</span>
                )}
              </div>
              {progressUpdate.currentQuestion && (
                <div className="text-sm mt-1 text-blue-700">
                  Current question: {progressUpdate.currentQuestion}
                </div>
              )}
              {progressUpdate.current !== undefined && progressUpdate.total !== undefined && (
                <div className="text-sm text-blue-700">
                  Progress: {progressUpdate.current} / {progressUpdate.total}
                  {progressUpdate.skippedCount !== undefined && progressUpdate.skippedCount > 0 && (
                    <span className="ml-2">
                      ({progressUpdate.skippedCount} cached, {progressUpdate.answersGenerated || 0} new)
                    </span>
                  )}
                </div>
              )}
            </AlertDescription>
            {progressUpdate.percentage !== undefined && (
              <Progress value={progressUpdate.percentage} className="h-2" />
            )}
          </div>
        </Alert>
      )}

      {/* Enhanced Generation Interface with Tabs */}
      <Tabs defaultValue="enhanced" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="enhanced">Enhanced (Question Categories)</TabsTrigger>
          <TabsTrigger value="quick">Quick Generation</TabsTrigger>
        </TabsList>

        {/* Enhanced Question Wheel Workflow */}
        <TabsContent value="enhanced" className="space-y-6">
          {viewMode === 'select-wheel' && (
            <Card>
              <CardHeader>
                <CardTitle>Select Question Wheel</CardTitle>
                <CardDescription>
                  Choose a Question Wheel to see question categories and generate targeted answers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Question Wheel</label>
                  <Select
                    value={selectedWheelId || ''}
                    onValueChange={(value) => {
                      setSelectedWheelId(value);
                      fetchCategories(value);
                      fetchJobs(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a question wheel" />
                    </SelectTrigger>
                    <SelectContent>
                      {questionSources
                        .filter(source => source.type === 'question_wheel')
                        .map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {questionSources.filter(s => s.type === 'question_wheel').length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No question wheels found. Create a Question Wheel in Question Wheel Research first.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {viewMode === 'select-category' && (
            <div className="space-y-4">
              <Button variant="outline" onClick={handleBackToWheelSelection}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Wheel Selection
              </Button>

              <QuestionTypeSelector
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={(category) => {
                  setSelectedCategory(category);
                  fetchQuestions(selectedWheelId!, category);
                }}
                loading={loadingCategories}
              />
            </div>
          )}

          {viewMode === 'preview-questions' && selectedCategory && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleBackToCategorySelection}>
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back to Categories
                  </Button>

                  {questions.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleSelectAll}
                        disabled={selectedQuestions.size === questions.length}
                      >
                        Select All ({questions.length})
                      </Button>

                      {selectedQuestions.size > 0 && (
                        <Button
                          variant="outline"
                          onClick={handleClearSelection}
                        >
                          Clear Selection
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {selectedQuestions.size > 0 && (
                  <div className="flex gap-2">
                    {(selectedCategory === 'product_experts' || selectedCategory === 'external_experts') && (
                      <Button onClick={() => {
                        navigate('/content-interviews', {
                          state: {
                            preselectedQuestions: Array.from(selectedQuestions).map(id =>
                              questions.find(q => q.id === id)
                            )
                          }
                        });
                      }}>
                        Create Content Interview ({selectedQuestions.size} questions)
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => {
                        // Get first selected question for AI Answer Test
                        const firstSelectedId = Array.from(selectedQuestions)[0];
                        const selectedQuestion = questions.find(q => q.id === firstSelectedId);
                        if (selectedQuestion) {
                          navigate('/ai-answer-test', {
                            state: {
                              question: selectedQuestion,
                              wheelId: selectedWheelId,
                              siteId: selectedSite?.id,
                              category: selectedCategory
                            }
                          });
                        }
                      }}
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      AI Answer Test
                    </Button>
                  </div>
                )}
              </div>

              <QuestionPreviewWithAnswers
                questions={questions}
                wheelId={selectedWheelId!}
                category={selectedCategory}
                onRefresh={() => fetchQuestions(selectedWheelId!, selectedCategory)}
                onSelectQuestion={handleSelectQuestion}
                selectedQuestions={selectedQuestions}
                onQuestionsUpdate={(sortedQuestions) => setQuestions(sortedQuestions)}
              />

              <div className="flex gap-4 flex-wrap">
                {(selectedCategory !== 'product_experts' && selectedCategory !== 'external_experts') && (
                  <Button
                    onClick={() => {
                      const category = categories.find(c => c.key === selectedCategory);
                      if (category) {
                        handleGenerateAnswers(category.generation_type as any);
                      }
                    }}
                    disabled={loading}
                  >
                    Generate All Answers
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleTransposeToProducts}
                  disabled={transposing || !selectedWheelId}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {transposing ? 'Generating...' : 'Generate Product Questions'}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Quick Generation Tab (Legacy) */}
        <TabsContent value="quick">
          <Card>
            <CardHeader>
              <CardTitle>Quick Answer Generation</CardTitle>
              <CardDescription>
                Select any question source and generate answers quickly
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Question Source Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Question Source</label>
                <Select value={selectedSourceId} onValueChange={setSelectedSourceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a question source" />
                  </SelectTrigger>
                  <SelectContent>
                    {questionSources.map((source) => (
                      <SelectItem key={source.id} value={source.id}>
                        {source.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Force Regenerate Option */}
              <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
                <Checkbox
                  id="forceRegenerate"
                  checked={forceRegenerate}
                  onCheckedChange={(checked) => setForceRegenerate(checked === true)}
                />
                <label
                  htmlFor="forceRegenerate"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Force regenerate all answers (ignore cached answers)
                </label>
              </div>

              {/* Generation Type Selection */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* SEO Expert Card - Recommended */}
                <Card className="relative hover:shadow-md transition-shadow cursor-pointer border-2 border-green-500 hover:border-green-600">
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-green-600 text-white">Recommended</Badge>
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-green-600" />
                      <CardTitle className="text-lg">SEO Expert</CardTitle>
                    </div>
                    <CardDescription>
                      Premium answers with benefit mapping, scientific context & product recommendations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Uses the same pipeline as AI Answer Test: benefit-attribute mapping, topic research, and SEO-optimized HTML output.
                    </p>
                    <Button
                      onClick={() => handleGenerateAnswers('seo_expert')}
                      disabled={loading || !selectedSourceId}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {loading ? 'Generating...' : 'Generate SEO Expert Answers'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Product Facts Card */}
                <Card className="relative hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-primary" />
                      <CardTitle className="text-lg">Product Facts</CardTitle>
                    </div>
                    <CardDescription>
                      Simpler answers using product data search
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Uses keyword/semantic search to find products. No benefit mapping or scientific context.
                    </p>
                    <Button
                      onClick={() => handleGenerateAnswers('product_facts')}
                      disabled={loading || !selectedSourceId}
                      className="w-full"
                      variant="outline"
                    >
                      {loading ? 'Generating...' : 'Generate with Product Facts'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Generic AI Card */}
                <Card className="relative hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-muted-foreground/30">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-muted-foreground" />
                      <CardTitle className="text-lg text-muted-foreground">Generic AI</CardTitle>
                    </div>
                    <CardDescription>
                      Fast answers using general AI knowledge (no product data)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Uses GPT-4o-mini without any product context. Best for non-product questions.
                    </p>
                    <Button
                      onClick={() => handleGenerateAnswers('generic_ai')}
                      disabled={loading || !selectedSourceId}
                      className="w-full"
                      variant="outline"
                    >
                      {loading ? 'Generating...' : 'Generate with Generic AI'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Product Questions Card */}
                <Card className="relative hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-orange-500">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-orange-500" />
                      <CardTitle className="text-lg">Product Questions</CardTitle>
                    </div>
                    <CardDescription>
                      Generate product-specific questions from category questions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Transforms "Best wheels for speed?" → "Is Mavic Cosmic SLR fast?"
                    </p>
                    <Button
                      onClick={handleTransposeToProducts}
                      disabled={transposing || !selectedSourceId}
                      className="w-full"
                      variant="outline"
                    >
                      {transposing ? 'Generating...' : 'Generate Product Questions'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Recent Jobs */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Recent Generation Jobs</CardTitle>
              <CardDescription>
                Track progress and review generated answers
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportJSON}
                disabled={jobs.length === 0}
              >
                <FileJson className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={jobs.length === 0}
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No generation jobs yet</p>
              <p className="text-sm">Create your first job above to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Card key={job.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(job.status)}>
                            {job.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {job.generation_type === 'generic_ai' ? 'Generic AI' :
                             job.generation_type === 'seo_expert' ? 'SEO Expert' : 'Product Facts'}
                          </span>
                        </div>
                        {job.wheel_name && (
                          <p className="text-sm font-medium">
                            {job.wheel_name}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Created {new Date(job.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/answer-approval/${job.id}`)}
                        >
                          Review Answers
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleOpenDeleteDialog(job)}
                        >
                          {job.status === 'processing' || job.status === 'pending' ? (
                            <>
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Progress */}
                    {job.status === 'processing' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Processing questions...</span>
                          <span>{job.processed_questions} / {job.total_questions}</span>
                        </div>
                        <Progress value={getProgress(job)} />
                      </div>
                    )}

                    {/* Completion Stats */}
                    {job.status === 'completed' && (
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold">{job.total_questions}</div>
                          <div className="text-sm text-muted-foreground">Total</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{job.approved_answers}</div>
                          <div className="text-sm text-muted-foreground">Approved</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">{job.rejected_answers}</div>
                          <div className="text-sm text-muted-foreground">Rejected</div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {jobToDelete?.status === 'processing' || jobToDelete?.status === 'pending'
                ? 'Cancel Job?'
                : 'Delete Job?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {jobToDelete?.status === 'processing' || jobToDelete?.status === 'pending' ? (
                <p>
                  This will cancel the currently running job. The job status will be marked as
                  cancelled and no further answers will be generated.
                </p>
              ) : (
                <>
                  <p>
                    Are you sure you want to delete this job? This action cannot be undone.
                  </p>
                  {(jobToDelete?.status === 'completed' || jobToDelete?.status === 'failed') && (
                    <div className="flex items-center space-x-2 p-3 bg-muted rounded-md">
                      <Checkbox
                        id="deleteAnswersCheckbox"
                        checked={deleteAnswers}
                        onCheckedChange={(checked) => setDeleteAnswers(checked === true)}
                      />
                      <label
                        htmlFor="deleteAnswersCheckbox"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        Also delete all generated answers for this job
                      </label>
                    </div>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {jobToDelete?.status === 'processing' || jobToDelete?.status === 'pending'
                ? 'Cancel Job'
                : 'Delete Job'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate Warning Dialog */}
      <AlertDialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Existing Answers Found
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <strong>{existingAnswersInfo?.existingCount || 0}</strong> of{' '}
                <strong>{existingAnswersInfo?.totalQuestions || 0}</strong> questions
                {existingAnswersInfo?.wheelName && (
                  <> in <em>"{existingAnswersInfo.wheelName}"</em></>
                )}{' '}
                already have generated answers.
              </p>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  If you continue, the existing answers will be <strong>overwritten</strong> with new ones.
                  This cannot be undone.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Tip: If you only want to generate answers for questions without existing answers,
                click "Cancel" and leave the "Force regenerate" checkbox unchecked.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPendingGenerationType(null);
              setExistingAnswersInfo(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRegenerate}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Overwrite Existing Answers
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
